import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Table,
  Grid3X3,
  Printer,
  ExternalLink,
  User,
  Package,
  Calendar,
  X,
  ArrowUpDown,
  Download,
  Eye,
  CheckCircle2,
  AlertCircle,
  Clock,
  Minus
} from 'lucide-react';
import jsPDF from 'jspdf';
import moment from 'moment';
import { Button, Input, Badge, Card, Select, getStatusConfig } from './SharedComponents';
// import { useVoice } from './voice/VoiceContext';  // Voice AI - DISABLED

const API_URL = import.meta.env.VITE_API_URL;

function InboundSearch() {
  // Voice AI integration - DISABLED
  // const { searchResults } = useVoice();
  const searchResults = null;  // Voice AI disabled

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState({});
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Label generation dialog
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [shelfAisle, setShelfAisle] = useState('');
  const [shelfHeight, setShelfHeight] = useState('');
  const [shelfDepth, setShelfDepth] = useState('');

  const navigate = useNavigate();

  // Watch for voice search results - DISABLED
  // useEffect(() => {
  //   console.log('[InboundSearch] useEffect triggered, searchResults:', searchResults);
  //   if (searchResults && searchResults.length > 0) {
  //     const formattedData = {};
  //     searchResults.forEach(inbound => {
  //       formattedData[inbound.id] = [{...}];
  //     });
  //     setData(formattedData);
  //     setSearchTerm(' ');
  //   }
  // }, [searchResults]);

  // Calculate match relevance score
  const calculateMatchScore = (item, searchTerms) => {
    let score = 0;
    const searchableFields = [
      { field: 'product_title', weight: 3 },
      { field: 'inbound_title', weight: 2 },
      { field: 'client_name', weight: 2 },
      { field: 'ASIN', weight: 2 },
      { field: 'SKU', weight: 2 },
      { field: 'FNSKU', weight: 2 },
      { field: 'inbound_id', weight: 1 },
      { field: 'notes', weight: 1 },
      { field: 'internal_notes', weight: 1 }
    ];

    searchTerms.forEach(term => {
      searchableFields.forEach(({ field, weight }) => {
        const fieldValue = String(item[field] || '').toLowerCase();
        if (fieldValue.includes(term)) {
          score += weight;
          if (fieldValue.startsWith(term)) score += weight * 0.5;
        }
      });
    });

    return score;
  };

  const getResultType = (item, searchTerms) => {
    const itemFields = ['product_title', 'ASIN', 'SKU', 'FNSKU'];
    const hasItemMatch = searchTerms.some(term => 
      itemFields.some(field => String(item[field] || '').toLowerCase().includes(term))
    );
    return hasItemMatch ? 'item' : 'inbound';
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/inbound-data`);
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Advanced search and filtering logic
  const processedResults = useMemo(() => {
    console.log('[processedResults] Computing results...');
    console.log('[processedResults] data keys:', Object.keys(data).length);
    console.log('[processedResults] searchTerm:', searchTerm);

    // If voice search provided results, show all data without search term requirement
    const isVoiceSearch = searchResults && searchResults.length > 0;
    console.log('[processedResults] isVoiceSearch:', isVoiceSearch);

    if (!isVoiceSearch && searchTerm.length < 2) {
      console.log('[processedResults] No results - need search term or voice search');
      return [];
    }

    const searchTerms = searchTerm.toLowerCase().split(' ').filter(term => term.length > 0);

    const results = Object.entries(data)
      .flatMap(([inboundId, items]) => {
        if (!Array.isArray(items)) {
          console.log('[processedResults] Skipping non-array for inbound:', inboundId);
          return [];
        }

        return items.map((item) => {
          const matchScore = calculateMatchScore(item, searchTerms);
          // For voice search, include all items even if matchScore is 0
          if (matchScore > 0 || isVoiceSearch) {
            return {
              ...item,
              matchScore: matchScore || 1,
              searchTerms,
              type: getResultType(item, searchTerms)
            };
          }
          return null;
        });
      })
      .filter(result => result !== null)
      .filter(result => statusFilter === 'all' || result.status === statusFilter)
      .filter(result => clientFilter === 'all' || result.client_name === clientFilter)
      .sort((a, b) => {
        if (sortBy === 'relevance') {
          return b.matchScore - a.matchScore;
        }
        const aVal = a[sortBy] || '';
        const bVal = b[sortBy] || '';
        if (sortOrder === 'desc') {
          return bVal.localeCompare ? bVal.localeCompare(aVal) : bVal - aVal;
        }
        return aVal.localeCompare ? aVal.localeCompare(bVal) : aVal - bVal;
      })
      .slice(0, 50);

    console.log('[processedResults] Final count:', results.length);
    return results;
  }, [searchTerm, data, statusFilter, clientFilter, sortBy, sortOrder, searchResults]);

  // Get unique values for filters
  const uniqueStatuses = [...new Set(Object.values(data).flat().map(item => item.status).filter(Boolean))];
  const uniqueClients = [...new Set(Object.values(data).flat().map(item => item.client_name).filter(Boolean))];


  const getQuantityConfig = (actual, expected) => {
    const actualNum = parseInt(actual) || 0;
    const expectedNum = parseInt(expected) || 0;
    
    if (expectedNum === 0) return { variant: 'default', label: 'No Expected', icon: '⚪' };
    if (actualNum === 0) return { variant: 'error', label: 'Not Received', icon: '🔴' };
    if (actualNum === expectedNum) return { variant: 'success', label: 'Complete', icon: '🟢' };
    if (actualNum < expectedNum) return { variant: 'warning', label: 'Partial', icon: '🟡' };
    if (actualNum > expectedNum) return { variant: 'info', label: 'Over-received', icon: '🔵' };
    return { variant: 'default', label: 'Unknown', icon: '⚪' };
  };

  // Highlight matching text
  const highlightMatch = (text, searchTerms) => {
    if (!text || !searchTerms) return String(text || '');
    const textStr = String(text);
    return searchTerms.reduce((acc, term) => {
      const regex = new RegExp(`(${term})`, 'gi');
      return acc.replace(regex, '<mark class="bg-yellow-200 px-1 border border-yellow-300">$1</mark>');
    }, textStr);
  };

  // Label generation
  const handleGenerateLabels = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'in',
      format: [2, 1]
    });

    const labels = [];
    for (let i = 1; i <= parseInt(shelfHeight); i++) {
      for (let j = 1; j <= parseInt(shelfDepth); j++) {
        labels.push(`${shelfAisle}-${i.toString().padStart(2, '0')}-${j.toString().padStart(2, '0')}`);
      }
    }

    labels.forEach((label, index) => {
      if (index > 0) doc.addPage();
      
      let fontSize = 36;
      doc.setFontSize(fontSize);
      let textWidth = doc.getTextWidth(label);
      
      while (textWidth > 1.8 && fontSize > 10) {
        fontSize -= 1;
        doc.setFontSize(fontSize);
        textWidth = doc.getTextWidth(label);
      }
      
      doc.text(label, 1, 0.5 + (fontSize / 72) / 2, { align: 'center' });
    });

    doc.save('shelf_labels.pdf');
    setOpenLabelDialog(false);
  };

  // Table view component
  const TableView = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
              Product Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Inbound Info
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
              Client
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
              Quantity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
              Created
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {processedResults.map((item, index) => (
            <tr 
              key={index}
              className="hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              onClick={() => navigate(`/inbounds/${item.inbound_id}`)}
            >
              <td className="px-6 py-4">
                <div className="flex items-start space-x-3">
                  {item.LARGE_IMG ? (
                    <img 
                      src={item.LARGE_IMG} 
                      alt="Product"
                      className="w-12 h-12 object-cover flex-shrink-0 border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div 
                      className="text-sm font-medium text-gray-900 line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html: highlightMatch(item.product_title || 'Untitled Product', item.searchTerms)
                      }}
                    />
                    <div className="mt-1 space-y-1">
                      {item.ASIN && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 font-medium">ASIN:</span>
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 font-mono border border-gray-300">{item.ASIN}</code>
                        </div>
                      )}
                      {item.SKU && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 font-medium">SKU:</span>
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 font-mono border border-gray-300">{item.SKU}</code>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              
              <td className="px-6 py-4">
                <div 
                  className="text-sm font-medium text-gray-900"
                  dangerouslySetInnerHTML={{
                    __html: highlightMatch(item.inbound_title || `Inbound #${item.inbound_id}`, item.searchTerms)
                  }}
                />
                <div className="text-xs text-gray-500">ID: {item.inbound_id}</div>
              </td>
              
              <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span 
                    className="text-sm text-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(item.client_name || 'Unknown', item.searchTerms)
                    }}
                  />
                </div>
              </td>
              
              <td className="px-6 py-4">
                {(() => {
                  const config = getStatusConfig(item.status);
                  return (
                    <Badge variant={config.variant}>
                      {config.label}
                    </Badge>
                  );
                })()}
              </td>
              
              <td className="px-6 py-4">
                {item.ACTUAL !== undefined && item.EXPECTED !== undefined ? (
                  (() => {
                    const config = getQuantityConfig(item.ACTUAL, item.EXPECTED);
                    return (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm">{config.icon}</span>
                        <span className="text-sm font-medium text-gray-900">
                          {item.ACTUAL} / {item.EXPECTED}
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <span className="text-sm text-gray-500">—</span>
                )}
              </td>
              
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{moment(item.created_at).format('MMM D, YYYY')}</div>
                <div className="text-xs text-gray-500">{moment(item.created_at).fromNow()}</div>
              </td>
              
              <td className="px-6 py-4 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/inbounds/${item.inbound_id}`, '_blank');
                  }}
                  className="p-2"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Cards view component
  const CardsView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {processedResults.map((item, index) => (
        <Card 
          key={index}
          className="p-6"
          onClick={() => navigate(`/inbounds/${item.inbound_id}`)}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3 flex-1">
              {item.LARGE_IMG ? (
                <img 
                  src={item.LARGE_IMG} 
                  alt="Product"
                  className="w-12 h-12  object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-100  flex items-center justify-center flex-shrink-0">
                  <Package className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 
                  className="text-sm font-medium text-gray-900 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: highlightMatch(item.inbound_title || `Inbound #${item.inbound_id}`, item.searchTerms)
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">{item.client_name}</p>
              </div>
            </div>
            {(() => {
              const config = getStatusConfig(item.status);
              return (
                <Badge variant={config.variant}>
                  {config.label}
                </Badge>
              );
            })()}
          </div>

          {item.type === 'item' && (
            <div className="bg-gray-50  p-3 mb-4">
              <p 
                className="text-sm font-medium text-gray-900 mb-2 line-clamp-2"
                dangerouslySetInnerHTML={{
                  __html: highlightMatch(item.product_title || 'Product', item.searchTerms)
                }}
              />
              <div className="grid grid-cols-2 gap-3 text-xs">
                {item.ASIN && (
                  <div>
                    <span className="text-gray-500 font-medium">ASIN</span>
                    <code className="block font-mono mt-1">{item.ASIN}</code>
                  </div>
                )}
                {item.ACTUAL !== undefined && item.EXPECTED !== undefined && (
                  <div>
                    <span className="text-gray-500 font-medium">Quantity</span>
                    <div className="flex items-center space-x-1 mt-1">
                      <span className="font-medium">{item.ACTUAL} / {item.EXPECTED}</span>
                      <span>{getQuantityConfig(item.ACTUAL, item.EXPECTED).icon}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {moment(item.created_at).fromNow()}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/inbounds/${item.inbound_id}`, '_blank');
              }}
              className="p-1"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Inbound Search
          </h1>
          <p className="text-gray-600">
            Search through inbound shipments, items, and client data
          </p>
        </div>

        {/* Search and Controls */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            {/* Search Input */}
            <div className="lg:col-span-6">
              <Input
                placeholder="Search inbounds, products, clients, ASINs, SKUs, FNSKUs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
              />
            </div>

            {/* View Mode Toggle */}
            <div className="lg:col-span-2">
              <div className="flex border border-gray-300 bg-white p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center justify-center px-3 py-1.5  text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <Table className="w-4 h-4 mr-1" />
                  Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center justify-center px-3 py-1.5  text-sm font-medium transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4 mr-1" />
                  Cards
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="lg:col-span-4 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button
                onClick={() => setOpenLabelDialog(true)}
                className="flex items-center"
              >
                <Printer className="w-4 h-4 mr-2" />
                Labels
              </Button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      ...uniqueStatuses.map(status => ({
                        value: status,
                        label: getStatusConfig(status).label
                      }))
                    ]}
                    placeholder="All Statuses"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <Select
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Clients' },
                      ...uniqueClients.map(client => ({
                        value: client,
                        label: client
                      }))
                    ]}
                    placeholder="All Clients"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={[
                      { value: 'relevance', label: 'Relevance' },
                      { value: 'created_at', label: 'Date Created' },
                      { value: 'inbound_title', label: 'Inbound Title' },
                      { value: 'client_name', label: 'Client Name' },
                      { value: 'status', label: 'Status' }
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <div className="flex border border-gray-300 bg-white p-1">
                    <button
                      onClick={() => setSortOrder('desc')}
                      className={`flex items-center justify-center px-3 py-1.5  text-sm font-medium transition-colors ${
                        sortOrder === 'desc'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <ArrowUpDown className="w-4 h-4 mr-1" />
                      Desc
                    </button>
                    <button
                      onClick={() => setSortOrder('asc')}
                      className={`flex items-center justify-center px-3 py-1.5  text-sm font-medium transition-colors ${
                        sortOrder === 'asc'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <ArrowUpDown className="w-4 h-4 mr-1 rotate-180" />
                      Asc
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Results */}
        <Card className="flex-1 flex flex-col">
          {/* Results Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-semibold text-gray-900">Search Results</h2>
              {processedResults.length > 0 && (
                <Badge variant="default" className="px-2 py-1">
                  {processedResults.length}
                </Badge>
              )}
            </div>
            
            {searchTerm && (
              <p className="text-sm text-gray-600 hidden md:block">
                {processedResults.length} results for "{searchTerm}"
              </p>
            )}
          </div>

          {/* Results Content */}
          <div className={`flex-1 ${viewMode === 'table' ? '' : 'p-6'} overflow-auto`}>
            {loading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-20 bg-gray-200 "></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : searchTerm.length < 2 && (!searchResults || searchResults.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Search className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Start typing to search</h3>
                <p className="text-gray-500 text-center max-w-md">
                  Enter at least 2 characters to search through inbounds, products, and clients
                </p>
              </div>
            ) : processedResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-blue-50 border border-blue-200  p-4 mb-4 max-w-md">
                  <p className="text-blue-800 text-center">
                    No results found for "{searchTerm}"
                  </p>
                </div>
                <p className="text-gray-500 text-center">
                  Try adjusting your search terms or filters
                </p>
              </div>
            ) : (
              viewMode === 'table' ? <TableView /> : <CardsView />
            )}
          </div>
        </Card>

        {/* Label Generation Dialog */}
        {openLabelDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generate Shelf Labels</h3>
                  <button
                    onClick={() => setOpenLabelDialog(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shelf Aisle (e.g., P, A, B)
                    </label>
                    <Input
                      value={shelfAisle}
                      onChange={(e) => setShelfAisle(e.target.value.toUpperCase())}
                      placeholder="P"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Height (Levels)
                      </label>
                      <Input
                        type="number"
                        value={shelfHeight}
                        onChange={(e) => setShelfHeight(e.target.value)}
                        placeholder="5"
                        min="1"
                        max="20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Depth (Positions)
                      </label>
                      <Input
                        type="number"
                        value={shelfDepth}
                        onChange={(e) => setShelfDepth(e.target.value)}
                        placeholder="10"
                        min="1"
                        max="50"
                      />
                    </div>
                  </div>
                  
                  {shelfAisle && shelfHeight && shelfDepth && (
                    <div className="bg-blue-50 border border-blue-200  p-3">
                      <p className="text-sm text-blue-800">
                        This will generate {parseInt(shelfHeight) * parseInt(shelfDepth)} labels 
                        from {shelfAisle}-01-01 to {shelfAisle}-{shelfHeight.padStart(2, '0')}-{shelfDepth.padStart(2, '0')}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setOpenLabelDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGenerateLabels}
                    disabled={!shelfAisle || !shelfHeight || !shelfDepth}
                    className="flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Generate PDF
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
  );
}

export default InboundSearch;